import axios from "axios";
import { BACKEND_ADDRESS } from "./constants";
import { setLocalStorageTokens, removeLocalStorageTokens } from "./tokenUtils";
import { apiRoutes } from "./apiRoutes";

// Lets the backend record which client a login happened from (see
// authController.js's getClientPlatform / User.lastLoginPlatform).
const appAxios = axios.create({
  baseURL: BACKEND_ADDRESS,
  headers: { "X-Client-Platform": "web" },
});

// Separate instance used only for token refresh — no interceptors, so it never
// recursively triggers the 401 handler if the refresh call itself fails.
const refreshAxios = axios.create({ baseURL: BACKEND_ADDRESS });

let logoutCallback = null;

export const setLogoutCallback = (fn) => {
  logoutCallback = fn;
};

// Refresh tokens are rotated server-side on every use (the old one is
// invalidated as soon as a new one is issued). Without de-duping, two
// requests that 401 around the same time would each fire their own refresh
// call — the second would arrive with an already-invalidated token, get
// rejected, and force-logout a session that the first call had just
// successfully renewed. Sharing one in-flight promise ensures only one
// refresh happens at a time; every other 401'd request just awaits it.
let refreshPromise = null;

// Only a definitive answer from the backend means the session is dead. A
// network error, timeout, or 5xx (e.g. mid-deploy) must not log the user out.
export const isSessionRejected = (err) =>
  [400, 401, 403].includes(err?.response?.status ?? err?.status);

const refreshAccessToken = (refreshToken) => {
  if (!refreshPromise) {
    refreshPromise = refreshAxios
      .post(apiRoutes.REFRESH_TOKEN, { refreshToken })
      .then((response) => {
        setLocalStorageTokens(response.data);
        return response.data.accessToken;
      })
      .catch((err) => {
        if (isSessionRejected(err)) {
          removeLocalStorageTokens();
          logoutCallback?.();
        }
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

appAxios.interceptors.request.use((req) => {
  const token = localStorage.getItem("accessToken");
  const expiresAt = localStorage.getItem("accessTokenExpiresAt");

  // Proactively refresh if the access token expires within 60 seconds.
  // We do this asynchronously via the response interceptor retry path rather
  // than inline here (keeping request interceptor synchronous for simplicity).
  req.headers.authorization = token ? `Bearer ${token}` : "";
  req._tokenExpiresAt = expiresAt ? Number(expiresAt) : null;

  // Requests that identify this session (autoLogin, logout) read the refresh
  // token here rather than at the call site, so the 401 retry below (which
  // runs this interceptor again) re-sends the token refresh just issued.
  // Re-sending the rotated-out one leaves the new session entry orphaned, and
  // orphans pile up against MAX_REFRESH_TOKENS, evicting other devices.
  if (req.sendRefreshToken) {
    const body =
      typeof req.data === "string" ? JSON.parse(req.data) : req.data || {};
    req.data = { ...body, refreshToken: localStorage.getItem("refreshToken") };
  }
  return req;
});

appAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only a request that carried a token can mean "the session just died".
    // A tokenless 401 (e.g. a query refetching mid-logout, before the UI has
    // switched to logged-out) must not force a logout: forceLogout clears the
    // query cache again, which silently cancels in-flight fetches (the feed)
    // and, since the user is already null, never re-renders to recover them.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.headers?.authorization
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        try {
          const newAccessToken = await refreshAccessToken(refreshToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return appAxios(originalRequest);
        } catch (_) {
          // Refresh failed — session is dead. Cleanup already ran inside
          // refreshAccessToken (once, regardless of how many requests were
          // awaiting the shared promise).
        }
      } else {
        // No refresh token available.
        removeLocalStorageTokens();
        logoutCallback?.();
      }
    }

    if (error.response) return Promise.reject(error.response);
    return Promise.reject(error);
  },
);

export default appAxios;
